import { useEffect, useRef, useState } from 'react';
import { LayerConfig } from '@/mocks/layers';
import { Product } from '@/mocks/products';
import ProductCard from './ProductCard';

interface LayerSectionProps {
  layer: LayerConfig;
  onOpen: (product: Product) => void;
}

const layerColors: Record<string, string> = {
  strategic: '#FF6B35',
  foundation: '#00D4FF',
  marketing: '#A855F7',
  fishery: '#10B981',
};

export default function LayerSection({ layer, onOpen }: LayerSectionProps) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.05, rootMargin: '-20px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const color = layerColors[layer.id] || '#00D4FF';

  return (
    <section
      id={layer.id}
      ref={ref}
      className="scroll-mt-24 px-6 md:px-12 py-16 md:py-20 max-w-7xl mx-auto"
    >
      <div
        className="mb-10 transition-all duration-700 ease-out"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transitionDelay: '0ms',
        }}
      >
        <div className="text-sm tracking-[0.35em] mb-4" style={{ color }}>
          LAYER
        </div>
        <h2 className="text-3xl md:text-5xl font-bold mb-4 text-white">{layer.layer}</h2>
        <p className="text-white/70 text-lg md:text-xl leading-8">{layer.desc}</p>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
        {layer.products.map((item, idx) => (
          <div
            key={item.id}
            className="transition-all duration-700 ease-out"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
              transitionDelay: `${300 + idx * 100}ms`,
            }}
          >
            <ProductCard product={item} index={idx} onOpen={onOpen} />
          </div>
        ))}
      </div>
    </section>
  );
}